import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { apiClient } from '@/services/api';
import { Download, Undo2, Calendar, Filter } from 'lucide-react';
import { useCurrentTemplate } from '@/hooks/useCurrentTemplate';

interface StockAdjustment {
  id: string;
  material: {
    name: string;
    id: string;
  };
  materialId: string;
  adjustmentType: 'increase' | 'decrease' | 'set';
  quantity: number;
  reason: string;
  notes?: string;
  unitCost: number;
  totalCostImpact: number;
  isWriteOff: boolean;
  writeOffCategory?: string;
  previousStock: number;
  newStock: number;
  actualQuantityChange: number;
  adjustmentDate: string;
  adjustedBy: string;
  createdAt: string;
  updatedAt: string;
  isUndone?: boolean; // Track if this adjustment has been undone
}

export const StockAdjustmentsReportPage: React.FC = () => {
  const { template: currentTemplate, loading: templateLoading } = useCurrentTemplate();
  const [adjustments, setAdjustments] = useState<StockAdjustment[]>([]);
  const [filteredAdjustments, setFilteredAdjustments] = useState<StockAdjustment[]>([]);
  const [loading, setLoading] = useState(false);
  const [undoingId, setUndoingId] = useState<string | null>(null);
  
  // Tab state
  const [activeTab, setActiveTab] = useState<'active' | 'undone' | 'all'>('active');
  
  // Date filtering state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchAdjustments();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [adjustments, startDate, endDate, activeTab]);

  // Helper function to identify undone adjustments
  const getUndoneAdjustmentIds = (adjustments: StockAdjustment[]): Set<string> => {
    const undoneIds = new Set<string>();
    
    // Find all undo operations
    const undoOperations = adjustments.filter(adj =>
      adj.reason === 'Undo adjustment' && adj.notes && adj.notes.includes('Original ID:')
    );
    
    // Extract original IDs from undo operations
    undoOperations.forEach(undo => {
      const match = undo.notes?.match(/Original ID: ([a-f0-9-]+)/);
      if (match) {
        undoneIds.add(match[1]);
      }
    });
    
    return undoneIds;
  };

  const applyFilters = () => {
    let filtered = [...adjustments];
    
    // Apply date filters first
    if (startDate) {
      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      filtered = filtered.filter(adj => new Date(adj.adjustmentDate) >= start);
    }
    
    if (endDate) {
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999);
      filtered = filtered.filter(adj => new Date(adj.adjustmentDate) <= end);
    }
    
    // Apply tab filters
    const undoneIds = getUndoneAdjustmentIds(adjustments);
    
    switch (activeTab) {
      case 'active':
        // Show only adjustments that haven't been undone and aren't undo operations themselves
        filtered = filtered.filter(adj =>
          adj.reason !== 'Undo adjustment' && !undoneIds.has(adj.id)
        );
        break;
      case 'undone':
        // Show only adjustments that have been undone OR are undo operations
        filtered = filtered.filter(adj =>
          adj.reason === 'Undo adjustment' || undoneIds.has(adj.id)
        );
        break;
      case 'all':
        // Show all adjustments (no additional filtering)
        break;
    }
    
    setFilteredAdjustments(filtered);
  };

  const applyDateFilters = applyFilters; // Keep for backward compatibility

  const setQuickDateRange = (range: 'thisMonth' | 'lastMonth' | 'thisYear' | 'lastYear') => {
    const now = new Date();
    let start: Date, end: Date;
    
    switch (range) {
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'lastMonth':
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'thisYear':
        start = new Date(now.getFullYear(), 0, 1);
        end = new Date(now.getFullYear(), 11, 31);
        break;
      case 'lastYear':
        start = new Date(now.getFullYear() - 1, 0, 1);
        end = new Date(now.getFullYear() - 1, 11, 31);
        break;
    }
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  // Helper function to calculate correct cost impact based on actual quantity change
  const calculateCorrectCostImpact = (adjustment: StockAdjustment): number => {
    const actualChange = Number(adjustment.actualQuantityChange) || 0;
    const unitCost = Number(adjustment.unitCost) || 0;
    
    // Cost impact should be: actualQuantityChange * unitCost
    // Positive for increases (adding inventory value)
    // Negative for decreases (reducing inventory value)
    return actualChange * unitCost;
  };

  // Helper function to safely format cost impact
  const formatCostImpact = (adjustment: StockAdjustment): string => {
    const correctCostImpact = calculateCorrectCostImpact(adjustment);
    return correctCostImpact.toFixed(2);
  };

  const fetchAdjustments = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/materials/stock-adjustments');
      console.log('Stock adjustments API response:', response);
      
      // Handle different response structures
      const adjustmentsData = response?.data || response || [];
      console.log('Processed adjustments data:', adjustmentsData);
      
      // Log first adjustment to see structure
      if (adjustmentsData.length > 0) {
        console.log('First adjustment structure:', adjustmentsData[0]);
        console.log('Cost fields in first adjustment:', {
          unitCost: adjustmentsData[0].unitCost,
          totalCostImpact: adjustmentsData[0].totalCostImpact,
          unitCostType: typeof adjustmentsData[0].unitCost,
          totalCostImpactType: typeof adjustmentsData[0].totalCostImpact
        });
      }
      
      setAdjustments(adjustmentsData);
    } catch (error) {
      console.error('Error fetching stock adjustments:', error);
      setAdjustments([]);
    } finally {
      setLoading(false);
    }
  };

  const undoAdjustment = async (adjustmentId: string) => {
    const adjustment = filteredAdjustments.find(adj => adj.id === adjustmentId);
    if (!adjustment) {
      alert('Adjustment not found.');
      return;
    }

    // Check if this adjustment has already been undone
    if (adjustment.isUndone || adjustment.reason === 'Undo adjustment') {
      alert('This adjustment has already been undone or is itself an undo operation.');
      return;
    }

    // More robust duplicate prevention: check if there's already an undo for this specific adjustment ID
    const existingUndo = adjustments.find(adj =>
      adj.reason === 'Undo adjustment' &&
      adj.notes?.includes(adjustment.id) && // Check if the original adjustment ID is in the notes
      adj.materialId === adjustment.materialId
    );

    if (existingUndo) {
      alert('This adjustment has already been undone. Check the adjustment history for the reversal record.');
      return;
    }

    if (!confirm(`Are you sure you want to undo this stock adjustment?\n\nThis will create a reverse adjustment:\n- Material: ${adjustment.material.name}\n- Original: ${adjustment.adjustmentType} ${adjustment.quantity}\n- Reverse: ${getReversalType(adjustment.adjustmentType, adjustment.actualQuantityChange)} ${Math.abs(adjustment.actualQuantityChange || 0)}\n\nThis action cannot be undone.`)) {
      return;
    }

    setUndoingId(adjustmentId);
    try {
      // Create a reverse adjustment
      const reverseAdjustment = createReverseAdjustment(adjustment);
      
      console.log('Sending reverse adjustment:', reverseAdjustment);
      
      const response = await apiClient.post('/materials/stock-adjustment-comprehensive', reverseAdjustment);
      
      console.log('Reverse adjustment response:', response);
      
      // Refresh the adjustments list
      await fetchAdjustments();
      
      alert('Stock adjustment has been successfully undone with a reverse adjustment.');
    } catch (error: any) {
      console.error('Error undoing stock adjustment:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Unknown error';
      alert(`Failed to undo stock adjustment: ${errorMessage}`);
    } finally {
      setUndoingId(null);
    }
  };

  const getReversalType = (originalType: string, actualChange: number): string => {
    if (actualChange > 0) return 'decrease';
    if (actualChange < 0) return 'increase';
    return originalType; // For 'set' operations with no change
  };

  const createReverseAdjustment = (originalAdjustment: StockAdjustment) => {
    const actualChange = originalAdjustment.actualQuantityChange || 0;
    const originalType = originalAdjustment.adjustmentType;
    const originalQuantity = originalAdjustment.quantity || 0;
    
    console.log('Creating reverse adjustment:', {
      originalType,
      originalQuantity,
      actualChange,
      previousStock: originalAdjustment.previousStock,
      newStock: originalAdjustment.newStock,
      originalAdjustment
    });
    
    // Determine the reverse adjustment type and quantity
    let reverseType: 'increase' | 'decrease' | 'set';
    let reverseQuantity: number;
    
    // CRITICAL FIX: Use the actual quantity change to determine the reverse operation
    if (originalType === 'increase') {
      // Original was increase, so reverse with decrease of the same amount
      reverseType = 'decrease';
      reverseQuantity = Math.abs(actualChange); // Use actual change, not original quantity
    } else if (originalType === 'decrease') {
      // Original was decrease, so reverse with increase of the same amount
      reverseType = 'increase';
      reverseQuantity = Math.abs(actualChange); // Use actual change, not original quantity
    } else if (originalType === 'set') {
      // For 'set' operations, we need to restore to the previous stock level
      const previousStock = originalAdjustment.previousStock || 0;
      reverseType = 'set';
      reverseQuantity = previousStock;
    } else {
      // Fallback - shouldn't happen
      reverseType = 'increase';
      reverseQuantity = Math.abs(actualChange);
    }

    console.log('Calculated reverse operation:', {
      reverseType,
      reverseQuantity,
      reasoning: `Original ${originalType} of ${originalQuantity} with actual change ${actualChange} -> Reverse ${reverseType} of ${reverseQuantity}`
    });

    const reverseAdjustmentData = {
      materialId: originalAdjustment.materialId,
      adjustmentType: reverseType,
      quantity: reverseQuantity,
      reason: 'Undo adjustment',
      notes: `Reversal of ${originalType} adjustment of ${originalQuantity} (actual change: ${actualChange}) from ${new Date(originalAdjustment.adjustmentDate).toLocaleDateString()}. Original ID: ${originalAdjustment.id}. Original reason: ${originalAdjustment.reason}`,
      unitCost: originalAdjustment.unitCost || 0,
      totalCostImpact: Math.abs(originalAdjustment.totalCostImpact || 0), // Use absolute value for cost impact
      isWriteOff: false, // Reversals are not write-offs
      writeOffCategory: undefined,
      adjustedBy: 'System (Undo Operation)'
    };

    console.log('Final reverse adjustment data:', reverseAdjustmentData);
    return reverseAdjustmentData;
  };

  const exportToCsv = () => {
    const dataToExport = filteredAdjustments;
    const dateRangeText = startDate && endDate
      ? `_${startDate}_to_${endDate}`
      : startDate
      ? `_from_${startDate}`
      : endDate
      ? `_to_${endDate}`
      : '';
    
    const headers = [
      'Date',
      'Material',
      'Adjustment Type',
      'Quantity',
      'Previous Stock',
      'New Stock',
      'Actual Change',
      'Unit Cost',
      'Total Cost Impact',
      'Reason',
      'Notes',
      'Write-Off',
      'Write-Off Category',
      'Adjusted By',
    ];
    
    const rows = dataToExport.map(adj => [
      new Date(adj.adjustmentDate).toLocaleDateString(),
      adj.material.name,
      adj.adjustmentType,
      adj.quantity,
      adj.previousStock || 'N/A',
      adj.newStock || 'N/A',
      adj.actualQuantityChange || 'N/A',
      adj.unitCost.toFixed(2),
      formatCostImpact(adj),
      adj.reason,
      adj.notes || '',
      adj.isWriteOff ? 'Yes' : 'No',
      adj.writeOffCategory || '',
      adj.adjustedBy,
    ]);

    // Add summary row for tax purposes
    const totalCostImpact = dataToExport.reduce((sum, adj) => {
      const cost = calculateCorrectCostImpact(adj);
      return sum + (adj.adjustmentType === 'decrease' ? Math.abs(cost) : 0); // Only count decreases for tax write-offs
    }, 0);

    const writeOffTotal = dataToExport
      .filter(adj => adj.isWriteOff)
      .reduce((sum, adj) => sum + Math.abs(calculateCorrectCostImpact(adj)), 0);

    rows.push([]);
    rows.push(['SUMMARY', '', '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push(['Total Adjustments', dataToExport.length.toString(), '', '', '', '', '', '', '', '', '', '', '', '']);
    rows.push(['Total Cost Impact (Decreases)', '', '', '', '', '', '', '', totalCostImpact.toFixed(2), '', '', '', '', '']);
    rows.push(['Total Write-Offs', '', '', '', '', '', '', '', writeOffTotal.toFixed(2), '', '', '', '', '']);

    let csvContent = "data:text/csv;charset=utf-8,"
      + headers.join(",") + "\n"
      + rows.map(e => e.join(",")).join("\n");

    const link = document.createElement('a');
    link.setAttribute('href', encodeURI(csvContent));
    link.setAttribute('download', `stock_adjustments_report${dateRangeText}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      <Helmet>
        <title>Stock Adjustments Report - {currentTemplate?.name || 'Business'} Inventory Tracker</title>
      </Helmet>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
              Stock Adjustments Report
              {currentTemplate && (
                <span className="text-lg font-normal text-gray-600 dark:text-gray-400 ml-3">
                  {currentTemplate.name}
                </span>
              )}
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-2">
              {currentTemplate
                ? `View and export stock adjustments for your ${currentTemplate.name.toLowerCase()} business. Only materials and adjustments relevant to ${currentTemplate.businessType} are shown.`
                : 'View and export a detailed history of all stock adjustments.'
              }
            </p>
            {filteredAdjustments.length !== adjustments.length && (
              <p className="text-sm text-jewelry-primary mt-1">
                Showing {filteredAdjustments.length} of {adjustments.length} adjustments
              </p>
            )}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              leftIcon={<Filter className="w-4 h-4" />}
            >
              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
            <Button onClick={exportToCsv} leftIcon={<Download className="w-4 h-4" />}>
              Export to CSV
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8 px-6" aria-label="Tabs">
              <button
                onClick={() => setActiveTab('active')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'active'
                    ? 'border-jewelry-primary text-jewelry-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Active Adjustments
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                  activeTab === 'active'
                    ? 'bg-jewelry-primary text-white'
                    : 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {adjustments.filter(adj => {
                    const undoneIds = getUndoneAdjustmentIds(adjustments);
                    return adj.reason !== 'Undo adjustment' && !undoneIds.has(adj.id);
                  }).length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('undone')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'undone'
                    ? 'border-jewelry-primary text-jewelry-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                Undone & Reversals
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                  activeTab === 'undone'
                    ? 'bg-jewelry-primary text-white'
                    : 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {adjustments.filter(adj => {
                    const undoneIds = getUndoneAdjustmentIds(adjustments);
                    return adj.reason === 'Undo adjustment' || undoneIds.has(adj.id);
                  }).length}
                </span>
              </button>
              <button
                onClick={() => setActiveTab('all')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'all'
                    ? 'border-jewelry-primary text-jewelry-primary'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'
                }`}
              >
                All Adjustments
                <span className={`ml-2 py-0.5 px-2 rounded-full text-xs ${
                  activeTab === 'all'
                    ? 'bg-jewelry-primary text-white'
                    : 'bg-gray-100 text-gray-900 dark:bg-gray-700 dark:text-gray-300'
                }`}>
                  {adjustments.length}
                </span>
              </button>
            </nav>
          </div>
          
          {/* Tab Description */}
          <div className="px-6 py-3 bg-gray-50 dark:bg-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {activeTab === 'active' && `Shows only adjustments that are currently in effect and impact your ${currentTemplate?.name || 'business'} inventory totals.`}
              {activeTab === 'undone' && 'Shows adjustments that have been reversed and their corresponding undo operations for audit purposes.'}
              {activeTab === 'all' && `Shows complete adjustment history for your ${currentTemplate?.name || 'business'} including active, undone, and undo operations.`}
            </p>
          </div>
        </div>

        {/* Date Filters */}
        {showFilters && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center gap-4 mb-4">
              <Calendar className="w-5 h-5 text-gray-500" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Filter by Date Range</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Start Date
                </label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  End Date
                </label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </div>
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => { setStartDate(''); setEndDate(''); }}
                  className="w-full"
                >
                  Clear Dates
                </Button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setQuickDateRange('thisMonth')}
              >
                This Month
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setQuickDateRange('lastMonth')}
              >
                Last Month
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setQuickDateRange('thisYear')}
              >
                This Year
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setQuickDateRange('lastYear')}
              >
                Last Year
              </Button>
            </div>
          </div>
        )}
        <div className="overflow-x-auto">
          <table className="min-w-full bg-white dark:bg-gray-800">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700">
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Date</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Material</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Type</th>
                <th className="py-3 px-4 border-b text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Quantity</th>
                <th className="py-3 px-4 border-b text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Previous</th>
                <th className="py-3 px-4 border-b text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">New Stock</th>
                <th className="py-3 px-4 border-b text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Change</th>
                <th className="py-3 px-4 border-b text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Reason</th>
                <th className="py-3 px-4 border-b text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Cost Impact</th>
                <th className="py-3 px-4 border-b text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Write-Off</th>
                <th className="py-3 px-4 border-b text-center text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={11} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    <div className="flex items-center justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mr-3"></div>
                      Loading adjustments...
                    </div>
                  </td>
                </tr>
              ) : filteredAdjustments.length === 0 ? (
                <tr>
                  <td colSpan={11} className="text-center py-8 text-gray-500 dark:text-gray-400">
                    {adjustments.length === 0 ? 'No stock adjustments found.' : 'No adjustments match the selected date range.'}
                  </td>
                </tr>
              ) : (
                filteredAdjustments.map(adj => {
                  const undoneIds = getUndoneAdjustmentIds(adjustments);
                  const isUndone = undoneIds.has(adj.id);
                  const isUndoOperation = adj.reason === 'Undo adjustment';
                  
                  return (
                  <tr key={adj.id} className={`hover:bg-gray-50 dark:hover:bg-gray-700 ${
                    isUndone || isUndoOperation ? 'opacity-60 bg-gray-50 dark:bg-gray-700/50' : ''
                  }`}>
                    <td className="py-3 px-4 border-b text-sm text-gray-900 dark:text-white">
                      {new Date(adj.adjustmentDate).toLocaleDateString()}
                    </td>
                    <td className="py-3 px-4 border-b text-sm font-medium text-gray-900 dark:text-white">
                      {adj.material.name}
                    </td>
                    <td className="py-3 px-4 border-b text-sm">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        adj.adjustmentType === 'increase'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                          : adj.adjustmentType === 'decrease'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                          : 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                      }`}>
                        {adj.adjustmentType}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-b text-sm text-right text-gray-900 dark:text-white">
                      {adj.quantity}
                    </td>
                    <td className="py-3 px-4 border-b text-sm text-right text-gray-900 dark:text-white">
                      {adj.previousStock ?? 'N/A'}
                    </td>
                    <td className="py-3 px-4 border-b text-sm text-right text-gray-900 dark:text-white">
                      {adj.newStock ?? 'N/A'}
                    </td>
                    <td className="py-3 px-4 border-b text-sm text-right">
                      <span className={`font-medium ${
                        (adj.actualQuantityChange ?? 0) > 0
                          ? 'text-green-600 dark:text-green-400'
                          : (adj.actualQuantityChange ?? 0) < 0
                          ? 'text-red-600 dark:text-red-400'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        {(adj.actualQuantityChange ?? 0) > 0 ? '+' : ''}{adj.actualQuantityChange ?? 'N/A'}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-b text-sm text-gray-900 dark:text-white">
                      {adj.reason}
                    </td>
                    <td className="py-3 px-4 border-b text-sm text-right font-medium text-gray-900 dark:text-white">
                      <span className={`${
                        calculateCorrectCostImpact(adj) < 0 ? 'text-red-600 dark:text-red-400' :
                        calculateCorrectCostImpact(adj) > 0 ? 'text-green-600 dark:text-green-400' :
                        'text-gray-600 dark:text-gray-400'
                      }`}>
                        {calculateCorrectCostImpact(adj) >= 0 ? '+' : ''}${formatCostImpact(adj)}
                      </span>
                    </td>
                    <td className="py-3 px-4 border-b text-center">
                      {adj.isWriteOff ? (
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                          Yes
                        </span>
                      ) : (
                        <span className="text-gray-400 dark:text-gray-500">No</span>
                      )}
                    </td>
                    <td className="py-3 px-4 border-b text-center">
                      {adj.reason === 'Undo adjustment' ? (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          Undo Record
                        </span>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => undoAdjustment(adj.id)}
                          disabled={undoingId === adj.id || adj.isUndone}
                          leftIcon={undoingId === adj.id ? (
                            <div className="w-3 h-3 border-2 border-gray-300 border-t-jewelry-primary rounded-full animate-spin" />
                          ) : (
                            <Undo2 className="w-3 h-3" />
                          )}
                          title={adj.isUndone ? "This adjustment has been undone" : "Undo this adjustment"}
                        >
                          {undoingId === adj.id ? 'Undoing...' : adj.isUndone ? 'Undone' : 'Undo'}
                        </Button>
                      )}
                    </td>
                  </tr>
                 );
               })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};